import {tva} from "@gluestack-ui/nativewind-utils/tva";
import type React from "react";
import {Fragment, memo} from "react";

import type {View} from "../../hooks/useView";
import {useView} from "../../hooks/useView";
import {ChevronLeftIcon, CloseIcon} from "../../react/components/Icons";
import {isReactElement} from "../../react/utils/isReactElement";
import {Box} from "../ui/box";
import {Button, ButtonIcon} from "../ui/button";
import {Heading} from "../ui/heading";
import {HStack} from "../ui/hstack";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

interface Props {
    action?: "back" | "close" | "none";
    title?: React.ReactNode;
    subTitle?: React.ReactNode;
    rightElement?: React.ReactNode;
    children?: React.ReactNode;
    onPress?: () => void;
}

const style = tva({
    base: "px-5 py-4 bg-white min-h-[58px] bg-background-0",
    variants: {
        view: {
            native: "min-h-[50px] py-3 border-b border-blueGray-200",
            webMobile: "border-b-4 border-solid border-blueGray-100",
            webDesktop: ""
        } as Record<View, string>
    }
});
const styleTitleWrapper = tva({
    base: "flex-1",
    variants: {
        view: {
            native: "",
            webMobile: "items-center",
            webDesktop: ""
        } satisfies Record<View, string>
    }
});
const subTitleStyle = tva({
    base: "px-5 py-4 bg-white",
    variants: {
        view: {
            native: "py-3 border-b border-blueGray-200",
            webMobile: "border-b-4 border-solid border-blueGray-100",
            webDesktop: ""
        } as Record<View, string>
    }
});
const styleElemLeft = tva({
    base: "",
    variants: {
        view: {
            native: "",
            webMobile: "absolute left-4 z-10",
            // the icon can't be visible on the current nativebase drawer
            // because of its current implementation so let's hide it until using the gluestack drawer
            webDesktop: "hidden"
        } satisfies Record<View, string>
    }
});
const styleElemRight = tva({
    base: "",
    variants: {
        view: {
            native: "",
            webMobile: "absolute right-4 z-10",
            webDesktop: ""
        } satisfies Record<View, string>
    }
});

// drawer on desktop
// modal on mobile vs web mobile view
const ModalHeader: React.FC<Props> = ({
    action = "close",
    title,
    subTitle,
    children,
    rightElement,
    onPress
}) => {
    const BackIcon = action === "back" ? ChevronLeftIcon : undefined;
    const LeftIcon = action === "close" ? CloseIcon : BackIcon;
    const testID = action === "close" ? "btn-close-modal" : "btn-back-modal";

    const view = useView();

    const titleHeader = (
        <HStack className={style({view})} space="xs" testID="modal-header">
            {LeftIcon && (
                <Button
                    action="default"
                    className={styleElemLeft({
                        view
                    })}
                    testID={testID}
                    variant="icon"
                    onPress={onPress}
                >
                    <ButtonIcon as={LeftIcon} />
                </Button>
            )}
            {!subTitle && (
                <>
                    {title ? (
                        <Box
                            className={styleTitleWrapper({
                                view
                            })}
                        >
                            <Text isHeading color="blueGray.800" variant="md">
                                {title}
                            </Text>
                        </Box>
                    ) : (
                        children
                    )}
                </>
            )}
            {rightElement && (
                <Box
                    className={styleElemRight({
                        view
                    })}
                >
                    {rightElement}
                </Box>
            )}
        </HStack>
    );

    if (subTitle) {
        return (
            <>
                {titleHeader}
                <VStack className={subTitleStyle({view})} space="sm">
                    <Heading color="blueGray.800" variant="lg">
                        {title}
                    </Heading>
                    {typeof subTitle === "string" || isReactElement(subTitle, Fragment) ? (
                        <Text isParagraph variant="sm">
                            {subTitle}
                        </Text>
                    ) : (
                        subTitle
                    )}
                </VStack>
            </>
        );
    }

    return titleHeader;
};

export default memo(ModalHeader);
